import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Warning, ArrowRight, ArrowLeft, ShoppingCart, CaretDown, Check, X, Info, Tag, Package } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResultsPage = () => {
  const { analysisId } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedParts, setSelectedParts] = useState([]);
  const [selectedStock, setSelectedStock] = useState({}); // { [partName]: [stockIndex, ...] }
  const [expandedPart, setExpandedPart] = useState(null);
  const [enriching, setEnriching] = useState(false);

  useEffect(() => { fetchAnalysis(); }, [analysisId]);

  // Poll for enrichment completion if status is "partial"
  useEffect(() => {
    if (analysis?.status !== 'partial') return;
    setEnriching(true);
    const interval = setInterval(async () => {
      try {
        const resp = await axios.get(`${API_URL}/api/analyses/${analysisId}`);
        if (resp.data.status === 'completed') {
          setAnalysis(resp.data);
          setSelectedParts(resp.data.detected_parts?.map(p => p.part_name) || []);
          setEnriching(false);
          clearInterval(interval);
        }
      } catch {}
    }, 4000);
    return () => clearInterval(interval);
  }, [analysis?.status, analysisId]);

  const fetchAnalysis = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/analyses/${analysisId}`);
      setAnalysis(response.data);
      setSelectedParts(response.data.detected_parts?.map(p => p.part_name) || []);
    } catch (error) {
      toast.error('Analyse non trouvée');
      navigate('/');
    } finally { setLoading(false); }
  };

  const togglePart = (partName) => {
    setSelectedParts(prev => prev.includes(partName) ? prev.filter(p => p !== partName) : [...prev, partName]);
  };

  const toggleStock = (partName, stockIndex) => {
    setSelectedStock(prev => {
      const current = prev[partName] || [];
      const next = current.includes(stockIndex) ? current.filter(i => i !== stockIndex) : [...current, stockIndex];
      // If a stock is being selected, ensure the parent part is also selected
      if (next.length > 0 && !selectedParts.includes(partName)) {
        setSelectedParts(p => p.includes(partName) ? p : [...p, partName]);
      }
      return { ...prev, [partName]: next };
    });
  };

  // Build flat list of selected stock refs (with part_name) to pass to QuotePage
  const buildSelectedStockRefs = () => {
    const refs = [];
    (analysis?.detected_parts || []).forEach((part) => {
      const indexes = selectedStock[part.part_name] || [];
      indexes.forEach((si) => {
        const item = part.stock_ecocasse?.[si];
        if (item) {
          refs.push({
            part_name: part.part_name,
            nom: item.nom,
            prix_ttc: item.prix_ttc,
            ref_oem: item.ref_oem || '',
            marque: item.marque || '',
            modele: item.modele || '',
            etat: item.etat || '',
            vignette: item.vignette || '',
          });
        }
      });
    });
    return refs;
  };

  const getConfidenceColor = (c) => c >= 80 ? 'text-[#2A9D8F]' : c >= 50 ? 'text-amber-500' : 'text-[#E63946]';
  const getConfidenceBg = (c) => c >= 80 ? 'bg-[#2A9D8F]/10 border-[#2A9D8F]/30' : c >= 50 ? 'bg-amber-100 border-amber-300' : 'bg-[#E63946]/10 border-[#E63946]/30';

  // B2C: show ALL detected parts (sinistre = damaged parts the client needs to replace)
  // Filtering damaged parts only applies to Pro B2B dashboard
  const displayParts = analysis?.detected_parts || [];

  // Prefer Ecocasse stock price (real price) over Gemini estimate
  const getPartPrice = (p) => p.stock_price || p.estimated_market_price || p.price || 0;

  const totalPrice = displayParts
    ?.filter(p => selectedParts.includes(p.part_name))
    ?.reduce((sum, p) => sum + getPartPrice(p), 0) || 0;

  const isPartSearch = analysis?.analysis_type === 'part';
  const isExpertise = analysis?.analysis_type === 'expertise';
  const accentColor = isPartSearch ? '#2A9D8F' : isExpertise ? '#8B5CF6' : '#E63946';

  if (loading) return <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center"><div className="w-12 h-12 border-2 border-[#E63946] border-t-transparent rounded-full animate-spin"></div></div>;
  if (!analysis) return null;

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <Header />
      <main className="pt-24 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <Link to="/analyse" className="inline-flex items-center gap-2 text-gray-400 hover:text-gray-900 text-sm mb-6" data-testid="back-to-analysis"><ArrowLeft size={16} />Nouvelle analyse</Link>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <span className="text-xs uppercase tracking-[0.3em] font-bold flex items-center gap-2" style={{ color: accentColor }}>
                  <CheckCircle size={16} weight="fill" />
                  {isPartSearch ? 'Pièce identifiée' : isExpertise ? 'Rapport analysé' : 'Analyse terminée'}
                </span>
                <h1 className="font-['Outfit'] text-2xl md:text-3xl font-bold text-gray-900 mt-2">
                  {isPartSearch ? 'Résultats de l\'identification' : isExpertise ? 'Pièces extraites du rapport' : 'Résultats de l\'analyse'}
                </h1>
                {analysis.vin && <p className="text-gray-500 mt-1">VIN : <span className="font-mono">{analysis.vin}</span></p>}
                {analysis.registration && <p className="text-gray-500 mt-1">Plaque : <span className="font-mono uppercase">{analysis.registration}</span></p>}
                {analysis.brand && <p className="text-gray-500 mt-1">{analysis.brand} {analysis.model} {analysis.year}</p>}
                {!analysis.brand && analysis.vehicle_identified?.brand && (
                  <p className="text-gray-500 mt-1" data-testid="vehicle-identified">
                    {analysis.vehicle_identified.brand} {analysis.vehicle_identified.model} {analysis.vehicle_identified.year}
                    {analysis.vehicle_identified.color && <span className="text-gray-400"> ({analysis.vehicle_identified.color})</span>}
                  </p>
                )}
              </div>
              <div className="flex gap-3">
                <button onClick={() => navigate('/analyse')} className="px-6 py-3 border border-gray-300 text-gray-700 text-sm font-bold uppercase hover:bg-gray-50" data-testid="new-analysis-btn">Nouvelle analyse</button>
                <button onClick={() => navigate(`/devis/${analysisId}`, { state: { selectedParts, selectedStockRefs: buildSelectedStockRefs() } })} disabled={!selectedParts.length} className={`px-6 py-3 text-sm font-bold uppercase flex items-center gap-2 transition-colors ${!selectedParts.length ? 'bg-gray-200 text-gray-400' : 'text-white hover:opacity-90'}`} style={{ backgroundColor: selectedParts.length ? accentColor : undefined }} data-testid="request-quote-btn"><ShoppingCart size={18} />Demander un devis</button>
              </div>
            </div>
          </motion.div>

          {/* Enrichment in progress banner */}
          {enriching && (
            <div className="bg-blue-50 border border-blue-200 px-5 py-3 mb-6 flex items-center gap-3" data-testid="enriching-banner">
              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
              <p className="text-blue-800 text-sm">Affinement des prix en cours... Les r&eacute;sultats se mettent &agrave; jour automatiquement.</p>
            </div>
          )}

          {analysis.damage_zones?.length > 0 && (
            <div className="bg-white border border-gray-200 p-6 mb-8 shadow-sm">
              <h3 className="text-gray-900 font-bold mb-3">Zones de dommages d&eacute;tect&eacute;es</h3>
              <div className="flex flex-wrap gap-2">{analysis.damage_zones.map((z, i) => <span key={i} className="px-3 py-1.5 bg-[#E63946]/10 border border-[#E63946]/30 text-[#E63946] text-sm">{z}</span>)}</div>
            </div>
          )}

          {/* Photos uploadées */}
          {analysis.image_paths?.length > 0 && (
            <div className="bg-white border border-gray-200 p-6 mb-8 shadow-sm" data-testid="uploaded-photos">
              <h3 className="text-gray-900 font-bold mb-3">Photos analys&eacute;es ({analysis.image_paths.length})</h3>
              <div className="flex flex-wrap gap-3">
                {analysis.image_paths.map((path, i) => {
                  const fname = path.includes('/') ? path.split('/').pop() : path;
                  return (
                    <div key={i} className="w-28 h-20 bg-gray-100 border border-gray-200 overflow-hidden rounded">
                      <img src={`${API_URL}/api/uploads/${fname}`} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-['Outfit'] text-lg font-bold text-gray-900">
                  {isPartSearch ? 'Pièces identifiées' : 'Pièces détectées'} 
                  <span className="text-gray-400 font-normal ml-2">({displayParts?.length || 0})</span>
                </h2>
                <button onClick={() => setSelectedParts(selectedParts.length === displayParts?.length ? [] : displayParts?.map(p => p.part_name) || [])} className="text-sm hover:underline" style={{ color: accentColor }} data-testid="toggle-all-parts">{selectedParts.length === displayParts?.length ? 'Tout décocher' : 'Tout cocher'}</button>
              </div>
              <div className="space-y-3">
                {displayParts?.map((part, i) => (
                  <div key={i} className="bg-white border shadow-sm transition-colors" style={{ borderColor: selectedParts.includes(part.part_name) ? `${accentColor}80` : '#E5E7EB' }}>
                    <div className="p-4 cursor-pointer" onClick={() => setExpandedPart(expandedPart === i ? null : i)}>
                      <div className="flex items-start gap-4">
                        <button onClick={(e) => { e.stopPropagation(); togglePart(part.part_name); }} className="w-6 h-6 border flex items-center justify-center transition-colors" style={{ backgroundColor: selectedParts.includes(part.part_name) ? accentColor : 'transparent', borderColor: selectedParts.includes(part.part_name) ? accentColor : '#D1D5DB' }} data-testid={`part-checkbox-${i}`}>{selectedParts.includes(part.part_name) && <Check size={14} className="text-white" />}</button>
                        <div className="flex-1">
                          <div className="flex flex-col sm:flex-row sm:justify-between gap-2">
                            <div>
                              <h4 className="text-gray-900 font-semibold">{part.part_name}</h4>
                              <span className="text-gray-500 text-sm">{part.category}{part.side && part.side !== 'null' && ` • ${part.side}`}</span>
                            </div>
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className={`px-2 py-1 text-xs font-bold border ${getConfidenceBg(part.confidence)} ${getConfidenceColor(part.confidence)}`}>{part.confidence}%</span>
                              {(part.stock_price || part.estimated_market_price) && (
                                <div className="flex items-center gap-1">
                                  <Tag size={14} className={part.stock_price ? "text-[#E63946]" : "text-[#2A9D8F]"} />
                                  <span className={`font-bold ${part.stock_price ? "text-[#E63946]" : "text-[#2A9D8F]"}`}>
                                    {(part.stock_price || part.estimated_market_price).toFixed(2)} €
                                  </span>
                                </div>
                              )}
                              <CaretDown size={16} className={`text-gray-400 transition-transform ${expandedPart === i ? 'rotate-180' : ''}`} />
                            </div>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {part.stock_ecocasse?.length > 0 && (
                              <span className="text-xs px-2 py-0.5 bg-[#E63946] text-white font-bold flex items-center gap-1" data-testid={`stock-badge-${i}`}>
                                <Package size={12} weight="fill" />EN STOCK ECOCASSE
                              </span>
                            )}
                            {part.condition && part.condition !== 'bon' && <span className={`text-xs px-2 py-0.5 border ${part.condition === 'neuf' || part.condition === 'très bon' ? 'bg-green-50 text-green-700 border-green-200' : part.condition === 'correct' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>{part.condition.charAt(0).toUpperCase() + part.condition.slice(1)}</span>}
                            {part.detection_source === 'vin_model' && <span className="text-indigo-600 text-xs px-2 py-0.5 bg-indigo-50 border border-indigo-200">Via VIN</span>}
                            {part.needs_verification && <span className="text-amber-500 text-xs flex items-center gap-1"><Warning size={12} />À vérifier</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                    {expandedPart === i && (
                      <div className="px-4 pb-4 border-t border-gray-100 pt-4 ml-10">
                        {part.justification && <p className="text-gray-700 text-sm mb-2">{part.justification}</p>}
                        {part.zone && <p className="text-gray-400 text-xs mb-2">Zone: {part.zone}</p>}
                        {part.oem_reference && part.oem_reference !== 'À vérifier' && (
                          <p className="text-gray-600 text-sm mb-2"><span className="font-semibold">Réf. OEM :</span> <span className="font-mono bg-gray-50 px-2 py-0.5 border border-gray-200 text-xs">{part.oem_reference}</span></p>
                        )}
                        {part.stock_ecocasse?.length > 0 && (
                          <div className="mt-4 bg-gradient-to-br from-[#E63946]/5 to-white border border-[#E63946]/30 p-4" data-testid={`stock-details-${i}`}>
                            <h5 className="text-[#E63946] font-bold text-sm uppercase tracking-wide mb-1 flex items-center gap-2">
                              <Package size={16} weight="fill" />
                              Disponible en stock Ecocasse
                            </h5>
                            <p className="text-gray-500 text-xs mb-3">Cliquez sur une pièce pour l'ajouter à votre devis avec son prix réel.</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {part.stock_ecocasse.slice(0, 4).map((stock, si) => {
                                const searchTerm = (stock.ref_oem || stock.nom || '').trim();
                                const ecocasseUrl = `https://www.ecocasse.fr/recherche?controller=search&s=${encodeURIComponent(searchTerm)}`;
                                const isStockChecked = (selectedStock[part.part_name] || []).includes(si);
                                return (
                                  <div
                                    key={si}
                                    onClick={(e) => { e.stopPropagation(); toggleStock(part.part_name, si); }}
                                    className={`flex flex-col bg-white border p-3 gap-2 cursor-pointer transition-all ${isStockChecked ? 'border-[#E63946] border-2 bg-[#E63946]/5' : 'border-gray-200 hover:border-[#E63946]/40'}`}
                                    data-testid={`stock-card-${i}-${si}`}
                                  >
                                    <div className="flex gap-3">
                                      <div className="relative flex-shrink-0">
                                        {stock.vignette ? (
                                          <img
                                            src={stock.vignette}
                                            alt={stock.nom}
                                            className="w-20 h-20 object-cover border border-gray-200"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                          />
                                        ) : (
                                          <div className="w-20 h-20 bg-gray-50 border border-gray-200 flex items-center justify-center">
                                            <Package size={28} className="text-gray-300" />
                                          </div>
                                        )}
                                        <div
                                          className={`absolute -top-2 -left-2 w-6 h-6 border-2 rounded-full flex items-center justify-center transition-all ${isStockChecked ? 'bg-[#E63946] border-[#E63946]' : 'bg-white border-gray-400'}`}
                                          data-testid={`stock-checkbox-${i}-${si}`}
                                        >
                                          {isStockChecked && <Check size={14} weight="bold" className="text-white" />}
                                        </div>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-gray-900 font-semibold text-sm truncate">{stock.nom}</p>
                                        {(stock.marque || stock.modele) && (
                                          <p className="text-gray-500 text-xs mt-0.5 truncate">{stock.marque} {stock.modele}</p>
                                        )}
                                        {stock.etat && (
                                          <span className="inline-block text-xs px-1.5 py-0.5 bg-green-50 text-green-700 border border-green-200 mt-1">État : {stock.etat}</span>
                                        )}
                                        <p className="text-[#E63946] font-bold text-base mt-1">{Number(stock.prix_ttc).toFixed(2)} €</p>
                                      </div>
                                    </div>
                                    <a
                                      href={ecocasseUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="w-full text-center py-2 border border-[#E63946] text-[#E63946] text-xs font-bold uppercase tracking-wide hover:bg-[#E63946] hover:text-white transition-colors flex items-center justify-center gap-1.5"
                                      data-testid={`stock-link-${i}-${si}`}
                                    >
                                      Voir sur Ecocasse.fr
                                      <ArrowRight size={12} weight="bold" />
                                    </a>
                                  </div>
                                );
                              })}
                            </div>
                            <p className="text-gray-500 text-xs mt-3 italic">
                              {part.stock_ecocasse.length > 4 ? `+${part.stock_ecocasse.length - 4} autres références disponibles. ` : ''}
                              Demandez un devis pour confirmer la compatibilité.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {!displayParts?.length && (
                  <div className="bg-white border border-green-200 p-8 text-center shadow-sm" data-testid="no-damage-message">
                    <CheckCircle size={48} className="text-green-500 mx-auto mb-4" weight="fill" />
                    <h3 className="text-gray-900 font-bold text-lg mb-2">Aucun dommage apparent</h3>
                    <p className="text-gray-500">Aucun dommage apparent constat&eacute; lors de l'inspection.</p>
                    <p className="text-gray-400 text-sm mt-2">Si vous pensez que des dommages existent, essayez de prendre des photos plus rapproch&eacute;es des zones concern&eacute;es.</p>
                  </div>
                )}
              </div>
            </div>
            <div className="lg:col-span-1">
              <div className="bg-white border border-gray-200 p-6 sticky top-24 shadow-sm">
                <h3 className="font-['Outfit'] text-lg font-bold text-gray-900 mb-4">Récapitulatif</h3>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Pièces sélectionnées</span>
                    <span className="text-gray-900">{selectedParts.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Estimation totale</span>
                    <span className="font-bold text-xl" style={{ color: accentColor }}>{totalPrice.toFixed(2)} €</span>
                  </div>
                </div>
                <p className="text-gray-400 text-xs mb-4">
                  Prix estimés sur le marché de l'occasion
                </p>
                <button onClick={() => navigate(`/devis/${analysisId}`, { state: { selectedParts, selectedStockRefs: buildSelectedStockRefs() } })} disabled={!selectedParts.length} className={`w-full py-4 text-sm font-bold uppercase flex items-center justify-center gap-2 transition-colors ${!selectedParts.length ? 'bg-gray-200 text-gray-400' : 'text-white hover:opacity-90'}`} style={{ backgroundColor: selectedParts.length ? accentColor : undefined }} data-testid="sidebar-quote-btn">Demander un devis<ArrowRight size={18} /></button>
                <div className="mt-6 p-3 bg-amber-50 border border-amber-200"><p className="text-amber-600 text-xs flex gap-2"><Warning size={14} />Estimation indicative. Devis définitif après vérification.</p></div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ResultsPage;
